'use client';

import { useRef } from 'react';
import {
  SAVINGS_DIP,
  SAVINGS_SLOTS,
  describePoint,
  monthsBefore,
  placeTooltip,
  savingsScale,
  useBoxWidth,
  useChartInspection,
} from '@/src/shared/lib/chart';
import { savingsRateText } from '@/src/shared/lib/figureFormat';
import { cn } from '@/src/shared/lib/shadcn';
import { usePrefersReducedMotion } from '@/src/shared/lib/usePrefersReducedMotion';
import { ChartAnnouncer, ChartPointTooltip, chartPointTooltipSize } from './chart-inspect';
import { SavingsGoalKey, SavingsMonthAxis, SavingsRateAxis } from './savings-rate-axis';
import type { SavingsRateBarsProps } from './savings-rate-bars.types';
import { SavingsRateColumn } from './savings-rate-column';

export type { SavingsRateBarsProps, SavingsRateLinks } from './savings-rate-bars.types';

const HEIGHT = 220;
// Below these column widths a range of many months stops writing every rate and month name.
const VALUE_MIN = 30;
const LABEL_MIN = 30;

/**
 * The landing's savings chart over real months: a column per month against the
 * goal line, on a scale labelled every ten points. Inspecting a column dims the
 * others and shows its income and spending.
 */
export function SavingsRateBars(props: SavingsRateBarsProps) {
  const { bars, locale, ariaLabel, labels, monthTitle, formatMoney, links } = props;
  const reduced = usePrefersReducedMotion();
  const boxRef = useRef<HTMLDivElement>(null);
  const width = useBoxWidth(boxRef, 600);
  // Slots before the first month keep a short history at the landing's spacing.
  const empty = monthsBefore(bars[0].month, Math.max(0, SAVINGS_SLOTS - bars.length));
  const cell = width / (empty.length + bars.length);
  // Only the real months take the pointer: an empty slot inspects nothing.
  const xs = bars.map((_, i) => (i + 0.5) * cell);
  const offset = empty.length * cell;
  const writesValues = cell >= VALUE_MIN;
  // Counted back from the latest month, so it always keeps its label.
  const labelStep = Math.max(1, Math.ceil(LABEL_MIN / cell));
  const { active, source, bind } = useChartInspection<HTMLDivElement>(
    xs,
    cell * bars.length,
    undefined,
    links?.open,
  );
  const action = links?.action(active, source) ?? null;
  const scale = savingsScale(bars.map((b) => b.pct));
  const share = (rate: number) => (rate / scale.hi) * 100;
  const dip = bars.some((b) => b.pct !== null && b.pct < 0) ? SAVINGS_DIP : 0;
  const written = (pct: number | null) => (pct === null ? '–' : savingsRateText(pct, locale));
  const last = bars.length - 1;
  const bar = active === null ? null : bars[active];
  const title = bar ? monthTitle(bar.month) : '';
  const rows = bar
    ? [
        {
          label: labels.rate,
          value: bar.pct === null ? labels.noIncome : written(bar.pct),
          strong: true,
        },
        { label: labels.income, value: formatMoney(bar.income) },
        { label: labels.expenses, value: formatMoney(bar.expenses) },
      ]
    : [];
  const tipOf = (pct: number | null) => share(pct !== null && pct > 0 ? pct : 0);
  const spot =
    bar === null || active === null
      ? null
      : placeTooltip(
          { x: offset + xs[active], y: HEIGHT * (1 - tipOf(bar.pct) / 100) },
          chartPointTooltipSize(title, rows, undefined, action !== null),
          { width, height: HEIGHT },
        );
  const opacityOf = (i: number) =>
    active === null ? (i === last ? 1 : 0.8) : i === active ? 1 : 0.35;

  return (
    <div>
      <SavingsGoalKey label={labels.goal} />
      <div className="relative pl-9" style={{ height: HEIGHT, marginBottom: dip && dip + 6 }}>
        <SavingsRateAxis scale={scale} locale={locale} reduced={reduced} />
        <div ref={boxRef} className="relative flex h-full">
          {empty.map((month, i) => (
            <SavingsRateColumn
              key={month}
              index={i}
              pct={null}
              scale={scale}
              plotHeight={HEIGHT}
              value={null}
              latest={false}
              opacity={1}
              reduced={reduced}
            />
          ))}
          <div
            role="img"
            aria-label={ariaLabel}
            className={cn(
              'flex h-full rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring',
              links?.linked(active) && 'cursor-pointer',
            )}
            {...bind}
            // As wide as its months together, so each column keeps the slot width.
            style={{ ...bind.style, flex: `${bars.length} 1 0%` }}
          >
            {bars.map((b, i) => (
              <SavingsRateColumn
                key={b.month}
                index={empty.length + i}
                pct={b.pct}
                scale={scale}
                plotHeight={HEIGHT}
                value={writesValues ? written(b.pct) : null}
                latest={i === last}
                opacity={opacityOf(i)}
                reduced={reduced}
              />
            ))}
          </div>
          {spot && (
            <ChartPointTooltip
              title={title}
              rows={rows}
              left={spot.left}
              top={spot.top}
              action={action}
            />
          )}
        </div>
      </div>
      <SavingsMonthAxis
        empty={empty}
        months={bars.map((b) => b.month)}
        locale={locale}
        labelStep={labelStep}
        active={active}
      />
      <ChartAnnouncer text={bar && source === 'keyboard' ? describePoint(title, rows) : null} />
    </div>
  );
}
